/**
 * Spec 007 / T168 — shared 429 → toast bridge.
 *
 * Surfaces the AI envelope's `rate_limited` 429 response as a Sonner toast
 * via TOAST_IDS.aiProposalRateLimitedError (same id for SSE consumer + every
 * SDK mutation hook so concurrent 429s deduplicate).
 *
 * Pure helpers — UI components compose these with `useTranslations` from
 * next-intl + the shared toast wrapper.
 */

import { TOAST_IDS } from '@/config/toast-ids';
import { showToast } from '@/lib/ui/toast';

const MIN_TO_MS = 60_000;

/**
 * Compute the minutes-until-reset value used by the rate-limit toast.
 *
 * - `resetMs` is the `details.reset` field from the AI envelope (unix-ms).
 * - We round UP so a "59 seconds remaining" surfaces as "1 minute" rather
 *   than rounding down to zero (which would tell the user to retry now,
 *   which would also rate-limit).
 * - The minimum displayed value is 1 minute — we never show "0 minutes".
 */
export function minutesUntilReset(resetMs: number, now: number = Date.now()): number {
  const deltaMs = resetMs - now;
  if (!Number.isFinite(deltaMs) || deltaMs <= 0) return 1;
  return Math.max(1, Math.ceil(deltaMs / MIN_TO_MS));
}

/** Compute the `Retry-After` value in seconds for the hook's error state. */
export function retryAfterSeconds(resetMs: number, now: number = Date.now()): number {
  const deltaMs = resetMs - now;
  if (!Number.isFinite(deltaMs) || deltaMs <= 0) return 0;
  return Math.max(0, Math.ceil(deltaMs / 1000));
}

export interface RateLimitedToastOptions {
  /** Pre-formatted toast text. Caller resolves via `useTranslations`. */
  message: string;
}

/** Surface the canonical 429 toast. Caller owns i18n formatting. */
export function showRateLimitedToast(opts: RateLimitedToastOptions): void {
  showToast({
    id: TOAST_IDS.aiProposalRateLimitedError,
    message: opts.message,
    variant: 'warning',
  });
}

/** Shape of the typed 429 metadata exposed on hook error state. */
export interface RateLimitInfo {
  /** Number of seconds until the rolling window resets. Always ≥ 0. */
  retryAfterSeconds: number;
  /** Unix-ms timestamp when the window resets (from `details.reset`). */
  resetMs: number;
}

/**
 * Parse a 429 envelope body.
 *
 * Returns `null` when the body does NOT match the AI envelope's
 * `rate_limited` shape — caller falls back to a generic error toast.
 *
 * The AI envelope shape is
 *   { error: { code: 'rate_limited', message, retryable: true,
 *              details: { reset: <unix-ms> } } }
 * (per `specs/007-ai-proposal-draft/supplementary/contracts/error-envelope.md`).
 */
// ALLOW-UNKNOWN: parses a free-form JSON error envelope from the network; runtime guards narrow the shape
export function parseRateLimitEnvelope(body: unknown): RateLimitInfo | null {
  if (typeof body !== 'object' || body === null) return null;
  // ALLOW-UNKNOWN: narrowing untyped JSON envelope — `error` is checked below
  const root = body as { error?: unknown };
  if (typeof root.error !== 'object' || root.error === null) return null;
  // ALLOW-UNKNOWN: narrowing untyped object — `code` and `details` are checked below
  const err = root.error as { code?: unknown; details?: unknown };
  if (err.code !== 'rate_limited') return null;
  if (typeof err.details !== 'object' || err.details === null) return null;
  // ALLOW-UNKNOWN: narrowing untyped JSON envelope from the network — `reset` is checked below
  const details = err.details as { reset?: unknown };
  if (typeof details.reset !== 'number') return null;
  return {
    resetMs: details.reset,
    retryAfterSeconds: retryAfterSeconds(details.reset),
  };
}

/**
 * Parse the SDK error's `details.reset` field. The SDK's
 * `SdkError.fromErrorEnvelope` already extracts `details` for us, so the
 * SDK callsites just need to read `error.details?.reset`.
 */
// ALLOW-UNKNOWN: `details` is a free-form JSON value handed to us by the SDK error envelope; we narrow with a runtime guard
export function parseSdkRateLimitDetails(details: unknown): RateLimitInfo | null {
  if (typeof details !== 'object' || details === null) return null;
  // ALLOW-UNKNOWN: narrowing untyped object — `reset` is checked below
  const d = details as { reset?: unknown };
  if (typeof d.reset !== 'number') return null;
  return { resetMs: d.reset, retryAfterSeconds: retryAfterSeconds(d.reset) };
}
